import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import {Box} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/Box';
import {ComponentDesc} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/ComponentDesc';
import {ChildLayoutModel} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/layouts/ChildLayoutModel';
import {DirectionType} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/layouts/hvbox/DirectionType';
import {LayoutModel} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/layouts/LayoutModel';
import {LayoutModelFieldPaths} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/layouts/LayoutModelFieldPaths';
import {PositioningType} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/layouts/PositioningType';
import {Prop} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/Prop';
import {RepeaterDisplayType} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/repeater/RepeaterDisplayType';
import {RepeaterModel} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/repeater/RepeaterModel';
import {RepeaterModelFieldPaths} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/repeater/RepeaterModelFieldPaths';
import {SystemPropsConfigs} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/SystemPropsConfigs';
import {Bounds} from '@mintdata/mint-kit/api/models/Bounds';
import {CompassDir} from '@mintdata/mint-kit/api/models/CompassDir';
import {LayoutHorizontal} from '@mintdata/mint-kit/api/models/LayoutHorizontal';
import {LayoutVertical} from '@mintdata/mint-kit/api/models/LayoutVertical';
import {PositiveUnits} from '@mintdata/mint-kit/api/models/PositiveUnits';
import {Units} from '@mintdata/mint-kit/api/models/Units';
import {UnitsType} from '@mintdata/mint-kit/api/models/UnitsType';
import {PropPath} from '@mintdata/mint-kit/api/properties/PropPath';
import {ANumber} from '@mintdata/mint-kit/api/values/ANumber';
import {APositiveNumber} from '@mintdata/mint-kit/api/values/APositiveNumber';
import {AString} from '@mintdata/mint-kit/api/values/AString';

export enum LayoutRecalc
{
    ALL = 'ALL',
    UNITS = 'UNITS',
    RESIZE = 'RESIZE',

    MOVED_VERTICAL = 'MOVED_VERTICAL',
    MOVED_HORIZONTAL = 'MOVED_HORIZONTAL',

    DISTRIBUTION_TOP = 'DISTRIBUTION_TOP',
    DISTRIBUTION_LEFT = 'DISTRIBUTION_LEFT',
    DISTRIBUTION_RIGHT = 'DISTRIBUTION_RIGHT',
    DISTRIBUTION_BOTTOM = 'DISTRIBUTION_BOTTOM',

    DISTRIBUTION_MIDDLE = 'DISTRIBUTION_MIDDLE',
    DISTRIBUTION_CENTER = 'DISTRIBUTION_CENTER',
}

export enum LayoutDistribution
{
    TOP = 'top',
    MIDDLE = 'middle',
    BOTTOM = 'bottom',

    LEFT = 'left',
    CENTER = 'center',
    RIGHT = 'right',
}

export interface LayoutRecalcDimensionsProps
{
    componentDesc:ComponentDesc;
    layoutRecalc:LayoutRecalc;

    box?:Box;
    boxParent?:Box;

    boxClientRect?:ClientRect;
    hostClientRect?:ClientRect;

    isWaitAnimationFrameIfNeed?:boolean;
}

export class LayoutUtils
{
    private static calcActions = {
        [SystemPropsConfigs.LAYOUT_TOP_PROP_NAME]: LayoutUtils.topChanged,
        [SystemPropsConfigs.LAYOUT_LEFT_PROP_NAME]: LayoutUtils.leftChanged,
        [SystemPropsConfigs.LAYOUT_RIGHT_PROP_NAME]: LayoutUtils.rightChanged,
        [SystemPropsConfigs.LAYOUT_BOTTOM_PROP_NAME]: LayoutUtils.bottomChanged,

        [SystemPropsConfigs.LAYOUT_WIDTH_PROP_NAME]: LayoutUtils.widthChanged,
        [SystemPropsConfigs.LAYOUT_HEIGHT_PROP_NAME]: LayoutUtils.heightChanged

        // [SystemPropsConfigs.LAYOUT_VERTICAL_PROP_NAME]: ,
        // [SystemPropsConfigs.LAYOUT_HORIZONTAL_PROP_NAME]: ,
        // [SystemPropsConfigs.LAYOUT_KEEP_PROPORTIONS_PROP_NAME]: ,
    };

    private static recalcToAnotherUnitsActions = {
        [SystemPropsConfigs.LAYOUT_TOP_PROP_NAME]: LayoutUtils.recalcTop,
        [SystemPropsConfigs.LAYOUT_LEFT_PROP_NAME]: LayoutUtils.recalcLeft,
        [SystemPropsConfigs.LAYOUT_RIGHT_PROP_NAME]: LayoutUtils.recalcRight,
        [SystemPropsConfigs.LAYOUT_BOTTOM_PROP_NAME]: LayoutUtils.recalcBottom,

        [SystemPropsConfigs.LAYOUT_WIDTH_PROP_NAME]: LayoutUtils.recalcWidth,
        [SystemPropsConfigs.LAYOUT_HEIGHT_PROP_NAME]: LayoutUtils.recalcHeight
    };

    private static distributionActions = {
        [LayoutDistribution.TOP]: LayoutUtils.distributionTop,
        [LayoutDistribution.MIDDLE]: LayoutUtils.distributionMiddle,
        [LayoutDistribution.BOTTOM]: LayoutUtils.distributionBottom,

        [LayoutDistribution.LEFT]: LayoutUtils.distributionLeft,
        [LayoutDistribution.CENTER]: LayoutUtils.distributionCenter,
        [LayoutDistribution.RIGHT]: LayoutUtils.distributionRight
    };

    private static recalcActions = {
        [LayoutRecalc.MOVED_HORIZONTAL]: LayoutUtils.recalcRight,
        [LayoutRecalc.MOVED_VERTICAL]: LayoutUtils.recalcBottom,

        [LayoutRecalc.DISTRIBUTION_TOP]: LayoutUtils.recalcBottom,
        [LayoutRecalc.DISTRIBUTION_MIDDLE]: LayoutUtils.recalcDistributionMiddle,
        [LayoutRecalc.DISTRIBUTION_BOTTOM]: LayoutUtils.recalcTop,

        [LayoutRecalc.DISTRIBUTION_LEFT]: LayoutUtils.recalcRight,
        [LayoutRecalc.DISTRIBUTION_CENTER]: LayoutUtils.recalcDistributionCenter,
        [LayoutRecalc.DISTRIBUTION_RIGHT]: LayoutUtils.recalcLeft
    };

    public static calcConstraints(prop:Prop<any>, prevValue:any, componentDesc:ComponentDesc):void
    {
        if( prop.name === SystemPropsConfigs.LAYOUT_PROP_NAME )
        {
            const oldValue:LayoutModel = (prevValue as LayoutModel);
            const newValue:LayoutModel = (prop.value as LayoutModel);

            LayoutUtils.layoutChanged(oldValue, newValue, componentDesc);
        }
        else if( prop.name === SystemPropsConfigs.CHILD_LAYOUT_PROP_NAME )
        {
            const oldValue:ChildLayoutModel = (prevValue as ChildLayoutModel);
            const newValue:ChildLayoutModel = (prop.value as ChildLayoutModel);

            LayoutUtils.childLayoutChanged(oldValue, newValue, componentDesc);
        }
        else
        {
            const fn = LayoutUtils.calcActions[prop.name];

            if( fn )
            {
                const oldValue = (prevValue as Units).value.value;
                const newValue = (prop.value as Units).value.value;

                const delta = oldValue - newValue;

                fn(delta, componentDesc);
            }
        }
    }

    private static topChanged(delta:number, componentDesc:ComponentDesc)
    {
        const {layoutVertical, layoutTop} = componentDesc;
        const deltaUnitsType = layoutTop.unitsType;

        if( LayoutVertical.isTop(layoutVertical) || LayoutVertical.isMiddle(layoutVertical) )
        {
            LayoutUtils.addDeltaValue(delta, componentDesc.layoutBottomProp, deltaUnitsType);
        }
        else if( LayoutVertical.isBottom(layoutVertical) || LayoutVertical.isTopBottom(layoutVertical) )
        {
            LayoutUtils.addDeltaValue(delta, componentDesc.layoutHeightProp, deltaUnitsType);
        }
    }

    private static bottomChanged(delta:number, componentDesc:ComponentDesc)
    {
        const {layoutVertical, layoutBottom} = componentDesc;
        const deltaUnitsType = layoutBottom.unitsType;

        if( LayoutVertical.isTop(layoutVertical) || LayoutVertical.isTopBottom(layoutVertical) )
        {
            LayoutUtils.addDeltaValue(delta, componentDesc.layoutHeightProp, deltaUnitsType);
        }
        else if( LayoutVertical.isBottom(layoutVertical) || LayoutVertical.isMiddle(layoutVertical) )
        {
            LayoutUtils.addDeltaValue(delta, componentDesc.layoutTopProp, deltaUnitsType);
        }
    }

    private static leftChanged(delta:number, componentDesc:ComponentDesc)
    {
        const {layoutHorizontal, layoutLeft} = componentDesc;
        const deltaUnitsType = layoutLeft.unitsType;

        if( LayoutHorizontal.isLeft(layoutHorizontal) || LayoutHorizontal.isCenter(layoutHorizontal) )
        {
            LayoutUtils.addDeltaValue(delta, componentDesc.layoutRightProp, deltaUnitsType);
        }
        else if( LayoutHorizontal.isRight(layoutHorizontal) || LayoutHorizontal.isLeftRight(layoutHorizontal) )
        {
            LayoutUtils.addDeltaValue(delta, componentDesc.layoutWidthProp, deltaUnitsType);
        }
    }

    private static rightChanged(delta:number, componentDesc:ComponentDesc)
    {
        const {layoutHorizontal, layoutRight} = componentDesc;
        const deltaUnitsType = layoutRight.unitsType;

        if( LayoutHorizontal.isLeft(layoutHorizontal) || LayoutHorizontal.isLeftRight(layoutHorizontal) )
        {
            LayoutUtils.addDeltaValue(delta, componentDesc.layoutWidthProp, deltaUnitsType);
        }
        else if( LayoutHorizontal.isRight(layoutHorizontal) || LayoutHorizontal.isCenter(layoutHorizontal) )
        {
            LayoutUtils.addDeltaValue(delta, componentDesc.layoutLeftProp, deltaUnitsType);
        }
    }

    private static widthChanged(delta:number, componentDesc:ComponentDesc)
    {
        const {layoutHorizontal, layoutWidth} = componentDesc;
        const deltaUnitsType = layoutWidth.unitsType;

        if( LayoutHorizontal.isLeft(layoutHorizontal) )
        {
            LayoutUtils.addDeltaValue(delta, componentDesc.layoutRightProp, deltaUnitsType);
        }
        else if( LayoutHorizontal.isRight(layoutHorizontal) )
        {
            LayoutUtils.addDeltaValue(delta, componentDesc.layoutLeftProp, deltaUnitsType);
        }
        else if( LayoutHorizontal.isLeftRight(layoutHorizontal) || LayoutHorizontal.isCenter(layoutHorizontal) )
        {
            const deltaLeft = Math.floor(delta / 2);
            const deltaRight = Math.ceil(delta / 2);

            const oldLeftValue = componentDesc.layoutLeft.value.value;
            const oldRightValue = componentDesc.layoutRight.value.value;

            // TODO учитывать %
            // если одна из пропертей в % - увеличение/уменьшение идет в сторону этой проперти
            // если обе проперти в % - увеличение/уменьшение идет ВСЕ ВРЕМЯ в сторону первой (left | top)

            const isDeltaLeft = Math.abs(oldLeftValue % 2) ^ Math.abs(oldRightValue % 2);

            LayoutUtils.addDeltaValue(isDeltaLeft ? deltaLeft : deltaRight, componentDesc.layoutLeftProp, deltaUnitsType);
            LayoutUtils.addDeltaValue(isDeltaLeft ? deltaRight : deltaLeft, componentDesc.layoutRightProp, deltaUnitsType);
        }
    }

    private static heightChanged(delta:number, componentDesc:ComponentDesc)
    {
        const {layoutVertical, layoutHeight} = componentDesc;
        const deltaUnitsType = layoutHeight.unitsType;

        if( LayoutVertical.isTop(layoutVertical) )
        {
            LayoutUtils.addDeltaValue(delta, componentDesc.layoutBottomProp, deltaUnitsType);
        }
        else if( LayoutVertical.isBottom(layoutVertical) )
        {
            LayoutUtils.addDeltaValue(delta, componentDesc.layoutTopProp, deltaUnitsType);
        }
        else if( LayoutVertical.isTopBottom(layoutVertical) || LayoutVertical.isMiddle(layoutVertical) )
        {
            const deltaTop = Math.floor(delta / 2);
            const deltaBottom = Math.ceil(delta / 2);

            const oldTopValue = componentDesc.layoutTop.value.value;
            const oldBottomValue = componentDesc.layoutBottom.value.value;

            // TODO учитывать %
            // если одна из пропертей в % - увеличение/уменьшение идет в сторону этой проперти
            // если обе проперти в % - увеличение/уменьшение идет ВСЕ ВРЕМЯ в сторону первой (left | top)

            const isDeltaTop = Math.abs(oldTopValue % 2) ^ Math.abs(oldBottomValue % 2);

            LayoutUtils.addDeltaValue(isDeltaTop ? deltaTop : deltaBottom, componentDesc.layoutTopProp, deltaUnitsType);
            LayoutUtils.addDeltaValue(isDeltaTop ? deltaBottom : deltaTop, componentDesc.layoutBottomProp, deltaUnitsType);
        }
    }

    private static addDeltaValue(delta:number, prop:Prop<Units>, deltaUnitsType:UnitsType):void
    {
        if( prop.value.unitsType.isEqualTo(deltaUnitsType) )
        {
            const oldValue = prop.value.value.value;
            const newValue = ANumber.fromNumber(oldValue + delta);

            const newFieldValue = new Units(newValue, prop.value.unitsType);
            prop.setPropValue(PropPath.ROOT, newFieldValue);
        }
        else
        {
            LayoutUtils.addDeltaValueToAnotherUnits(prop);
        }
    }

    public static addDeltaValueToAnotherUnits(prop:Prop<Units>):void
    {
        const designState = commonControllers.designState;
        const box:Box = designState.findBoxFromId(prop.box.id);

        if( !box || box.isViewport ) return;

        // hostClientRect нужен при любых расчетах
        // потому что у парента еще есть margin, padding, border-width
        const hostClientRect = LayoutUtils.getParentHostClientRect(box);
        if( !hostClientRect ) return;
        //
        if( hostClientRect.width === 0 || hostClientRect.height === 0 ) return; // TODO ??? in NOT active TAB

        const {componentDesc} = box;

        // можно перевести delta из px -> %
        // но из % -> px переводить нельзя - будет погрешность отрисовки
        // поэтому delta не переводим в нужный Units - а пересчитываем всю проперти

        const fn = LayoutUtils.recalcToAnotherUnitsActions[prop.name];

        fn({
            componentDesc,
            layoutRecalc: LayoutRecalc.UNITS,
            hostClientRect
        });
    }

    public static distribution(distribution:LayoutDistribution, componentDesc:ComponentDesc):void
    {
        const fn = LayoutUtils.distributionActions[distribution];

        fn && fn(componentDesc);
    }

    private static distributionTop(componentDesc:ComponentDesc)
    {
        const {layoutTop} = componentDesc;

        const deltaUnitsType = layoutTop.unitsType;
        const top = layoutTop.value.value;

        LayoutUtils.addDeltaValue(-top, componentDesc.layoutTopProp, deltaUnitsType);
        // LayoutUtils.addDeltaValue(top, componentDesc.layoutBottomProp);
        LayoutUtils.recalcDimensions({componentDesc, layoutRecalc: LayoutRecalc.DISTRIBUTION_TOP});
    }

    private static distributionMiddle(componentDesc:ComponentDesc)
    {
        if( componentDesc.layoutTop.unitsType.isEqualTo(componentDesc.layoutBottom.unitsType) )
        {
            const {layoutTop, layoutBottom} = componentDesc;

            const deltaUnitsType = layoutTop.unitsType;
            const top = layoutTop.value.value;
            const bottom = layoutBottom.value.value;

            const delta = (top + bottom) / 2;
            const deltaTop = Math.ceil(delta);
            const deltaBottom = Math.floor(delta);

            LayoutUtils.addDeltaValue(deltaTop - top, componentDesc.layoutTopProp, deltaUnitsType);
            LayoutUtils.addDeltaValue(deltaBottom - bottom, componentDesc.layoutBottomProp, deltaUnitsType);
        }
        else
        {
            LayoutUtils.recalcDimensions({componentDesc, layoutRecalc: LayoutRecalc.DISTRIBUTION_MIDDLE});
        }
    }

    private static distributionBottom(componentDesc:ComponentDesc)
    {
        const {layoutBottom} = componentDesc;

        const deltaUnitsType = layoutBottom.unitsType;
        const bottom = layoutBottom.value.value;

        // LayoutUtils.addDeltaValue(bottom, componentDesc.layoutTopProp);
        LayoutUtils.addDeltaValue(-bottom, componentDesc.layoutBottomProp, deltaUnitsType);
        LayoutUtils.recalcDimensions({componentDesc, layoutRecalc: LayoutRecalc.DISTRIBUTION_BOTTOM});
    }

    private static distributionLeft(componentDesc:ComponentDesc)
    {
        const {layoutLeft} = componentDesc;

        const deltaUnitsType = layoutLeft.unitsType;
        const left = layoutLeft.value.value;

        LayoutUtils.addDeltaValue(-left, componentDesc.layoutLeftProp, deltaUnitsType);
        // LayoutUtils.addDeltaValue(left, componentDesc.layoutRightProp);
        LayoutUtils.recalcDimensions({componentDesc, layoutRecalc: LayoutRecalc.DISTRIBUTION_LEFT});
    }

    private static distributionCenter(componentDesc:ComponentDesc)
    {
        if( componentDesc.layoutLeft.unitsType.isEqualTo(componentDesc.layoutRight.unitsType) )
        {
            const {layoutLeft, layoutRight} = componentDesc;

            const deltaUnitsType = layoutLeft.unitsType;
            const left = layoutLeft.value.value;
            const right = layoutRight.value.value;

            const delta = (left + right) / 2;
            const deltaLeft = Math.ceil(delta);
            const deltaRight = Math.floor(delta);

            LayoutUtils.addDeltaValue(deltaLeft - left, componentDesc.layoutLeftProp, deltaUnitsType);
            LayoutUtils.addDeltaValue(deltaRight - right, componentDesc.layoutRightProp, deltaUnitsType);
        }
        else
        {
            LayoutUtils.recalcDimensions({componentDesc, layoutRecalc: LayoutRecalc.DISTRIBUTION_CENTER});
        }
    }

    private static distributionRight(componentDesc:ComponentDesc)
    {
        const {layoutRight} = componentDesc;

        const deltaUnitsType = layoutRight.unitsType;
        const right = layoutRight.value.value;

        // LayoutUtils.addDeltaValue(right, componentDesc.layoutLeftProp);
        LayoutUtils.addDeltaValue(-right, componentDesc.layoutRightProp, deltaUnitsType);
        LayoutUtils.recalcDimensions({componentDesc, layoutRecalc: LayoutRecalc.DISTRIBUTION_RIGHT});
    }

    public static recalcDimensions(props:LayoutRecalcDimensionsProps)
    {
        const {componentDesc, layoutRecalc, isWaitAnimationFrameIfNeed} = props;
        let {hostClientRect} = props;

        const designState = commonControllers.designState;
        const box:Box = designState.findBoxFromId(componentDesc.box.id);

        if( !box || box.isViewport ) return;

        // hostClientRect нужен при любых расчетах
        // потому что у парента еще есть margin, padding, border-width
        if( !hostClientRect ) hostClientRect = LayoutUtils.getParentHostClientRect(box);
        if( !hostClientRect ) return;

        if( hostClientRect.width === 0 || hostClientRect.height === 0 ) return; // TODO ??? in NOT active TAB

        props.hostClientRect = hostClientRect;

        // const recalcProps = {
        //     ...props,
        //     hostClientRect
        // };

        let fn:(_props:LayoutRecalcDimensionsProps) => void;

        switch( layoutRecalc )
        {
            // selected component -> recalc props for property panel
            case LayoutRecalc.ALL:
                // if( LayoutUtils.areLayoutPropsHasPercentages(box) )
                // {
                //     // boxClientRect нужен только если одно из пропертей в процентах
                //     // if( !boxClientRect ) boxClientRect = LayoutUtils.getBoxClientRect(box);
                //     recalcProps.boxClientRect = LayoutUtils.getBoxClientRect(box);
                //     if( !recalcProps.boxClientRect ) return;
                // }
                fn = (_props:LayoutRecalcDimensionsProps) =>
                {
                    LayoutUtils.recalcVertical(_props);
                    LayoutUtils.recalcHorizontal(_props);
                };
                break;

            case LayoutRecalc.MOVED_HORIZONTAL:
            case LayoutRecalc.MOVED_VERTICAL:
            case LayoutRecalc.DISTRIBUTION_TOP:
            case LayoutRecalc.DISTRIBUTION_MIDDLE:
            case LayoutRecalc.DISTRIBUTION_BOTTOM:
            case LayoutRecalc.DISTRIBUTION_LEFT:
            case LayoutRecalc.DISTRIBUTION_CENTER:
            case LayoutRecalc.DISTRIBUTION_RIGHT:

                fn = LayoutUtils.recalcActions[layoutRecalc];
                break;
        }

        // const recalcFn = () =>
        // {
        // if( isWaitAnimationFrameIfNeed && LayoutUtils.areLayoutPropsHasPercentages(box) )
        // {
        //     // boxClientRect нужен только если одно из пропертей в процентах
        //     recalcProps.boxClientRect = LayoutUtils.getBoxClientRect(box);
        //     if( !recalcProps.boxClientRect ) return;
        // }

        // console.log('%c recalcProps =', 'background:#0f0;color:#000;', recalcProps);

        // fn(recalcProps);
        fn(props);
        // };

        // if( isWaitAnimationFrameIfNeed )
        //     requestAnimationFrame(recalcFn);
        //     // setTimeout(recalcFn, 1000);
        // else
        // recalcFn();

        // console.log('%c recalcDimensions =----------------------------------------', 'background:#ff0;color:#000;');
    }

    public static recalcVertical(props:LayoutRecalcDimensionsProps)
    {
        const {componentDesc} = props;
        const {layoutVertical} = componentDesc;
        // const {area: {zoom}} = rootState.ui.visualDesigner.surfaceEditor;

        if( LayoutVertical.isTop(layoutVertical) )
        {
            LayoutUtils.recalcBottom(props);
        }
        else if( LayoutVertical.isBottom(layoutVertical) )
        {
            LayoutUtils.recalcTop(props);
        }
        else if( LayoutVertical.isTopBottom(layoutVertical) )
        {
            // скорее всего тут не надо пересчитывать top/left/right/bottom
            // т.к. значения не зависят от изменений размера парента
            LayoutUtils.recalcHeight(props);
        }
        else if( LayoutVertical.isMiddle(layoutVertical) )
        {
            // TODO
        }
    }

    public static recalcHorizontal(props:LayoutRecalcDimensionsProps)
    {
        const {componentDesc} = props;
        const {layoutHorizontal} = componentDesc;
        // const {area: {zoom}} = rootState.ui.visualDesigner.surfaceEditor;

        if( LayoutHorizontal.isLeft(layoutHorizontal) )
        {
            LayoutUtils.recalcRight(props);
        }
        else if( LayoutHorizontal.isRight(layoutHorizontal) )
        {
            LayoutUtils.recalcLeft(props);
        }
        else if( LayoutHorizontal.isLeftRight(layoutHorizontal) )
        {
            // скорее всего тут не надо пересчитывать top/left/right/bottom
            // т.к. значения не зависят от изменений размера парента
            LayoutUtils.recalcWidth(props);
        }
        else if( LayoutHorizontal.isCenter(layoutHorizontal) )
        {
            // TODO
        }
    }

    public static recalcHeight(props:LayoutRecalcDimensionsProps)
    {
        const {componentDesc, hostClientRect} = props;
        const {layoutTop, layoutBottom, layoutHeight} = componentDesc;

        const hostHeight = hostClientRect.height;

        let top;
        let bottom;
        let height;

        if( UnitsType.percentages().isEqualTo(layoutTop.unitsType) )
            top = hostHeight * layoutTop.value.value / 100;
        else
            top = layoutTop.value.value;

        if( UnitsType.percentages().isEqualTo(layoutBottom.unitsType) )
            bottom = hostHeight * layoutBottom.value.value / 100;
        else
            bottom = layoutBottom.value.value;

        height = (hostHeight - Math.round(top) - Math.round(bottom));

        if( UnitsType.percentages().isEqualTo(layoutHeight.unitsType) )
        {
            height = (height / hostHeight) * 100;
        }

        if( height < 0 ) height = 0;

        if( layoutHeight.value.value !== height )
        {
            const {layoutHeightProp} = componentDesc;

            const newValue = APositiveNumber.fromNumber(height);
            const newFieldValue = new Units(newValue, layoutHeight.unitsType);

            layoutHeightProp.setPropValue(PropPath.ROOT, newFieldValue);
        }
    }

    public static recalcWidth(props:LayoutRecalcDimensionsProps)
    {
        const {componentDesc, hostClientRect} = props;
        const {layoutLeft, layoutRight, layoutWidth} = componentDesc;

        const hostWidth = hostClientRect.width;

        let left;
        let right;
        let width;

        if( UnitsType.percentages().isEqualTo(layoutLeft.unitsType) )
            left = hostWidth * layoutLeft.value.value / 100;
        else
            left = layoutLeft.value.value;

        if( UnitsType.percentages().isEqualTo(layoutRight.unitsType) )
            right = hostWidth * layoutRight.value.value / 100;
        else
            right = layoutRight.value.value;

        width = (hostWidth - Math.round(left) - Math.round(right));

        if( UnitsType.percentages().isEqualTo(layoutWidth.unitsType) )
        {
            width = (width / hostWidth) * 100;
        }

        if( width < 0 ) width = 0;

        if( layoutWidth.value.value !== width )
        {
            const {layoutWidthProp} = componentDesc;

            const newValue = APositiveNumber.fromNumber(width);
            const newFieldValue = new Units(newValue, layoutWidth.unitsType);

            layoutWidthProp.setPropValue(PropPath.ROOT, newFieldValue);
        }
    }

    public static recalcDistributionMiddle(props:LayoutRecalcDimensionsProps)
    {
        const {componentDesc, hostClientRect} = props;
        const {layoutTop, layoutBottom, layoutHeight} = componentDesc;

        const hostHeight = hostClientRect.height;

        let top;
        let height;
        let bottom;

        if( UnitsType.percentages().isEqualTo(layoutHeight.unitsType) )
            height = hostHeight * layoutHeight.value.value / 100;
        else
            height = layoutHeight.value.value;

        top = bottom = (hostHeight - height) / 2;

        if( UnitsType.percentages().isEqualTo(layoutTop.unitsType) )
        {
            top = (top / hostHeight) * 100;
        }

        if( UnitsType.percentages().isEqualTo(layoutBottom.unitsType) )
        {
            bottom = (bottom / hostHeight) * 100;
        }

        if( layoutTop.value.value !== top )
        {
            const {layoutTopProp} = componentDesc;

            const newValue = ANumber.fromNumber(top);
            const newFieldValue = new Units(newValue, layoutTop.unitsType);

            layoutTopProp.setPropValue(PropPath.ROOT, newFieldValue);
        }

        if( layoutBottom.value.value !== bottom )
        {
            const {layoutBottomProp} = componentDesc;

            const newValue = ANumber.fromNumber(bottom);
            const newFieldValue = new Units(newValue, layoutBottom.unitsType);

            layoutBottomProp.setPropValue(PropPath.ROOT, newFieldValue);
        }
    }

    public static recalcDistributionCenter(props:LayoutRecalcDimensionsProps)
    {
        const {componentDesc, hostClientRect} = props;
        const {layoutLeft, layoutRight, layoutWidth} = componentDesc;

        const hostWidth = hostClientRect.width;

        let left;
        let width;
        let right;

        if( UnitsType.percentages().isEqualTo(layoutWidth.unitsType) )
            width = hostWidth * layoutWidth.value.value / 100;
        else
            width = layoutWidth.value.value;

        left = right = (hostWidth - width) / 2;

        if( UnitsType.percentages().isEqualTo(layoutLeft.unitsType) )
        {
            left = (left / hostWidth) * 100;
        }

        if( UnitsType.percentages().isEqualTo(layoutRight.unitsType) )
        {
            right = (right / hostWidth) * 100;
        }

        if( layoutLeft.value.value !== left )
        {
            const {layoutLeftProp} = componentDesc;

            const newValue = ANumber.fromNumber(left);
            const newFieldValue = new Units(newValue, layoutLeft.unitsType);

            layoutLeftProp.setPropValue(PropPath.ROOT, newFieldValue);
        }

        if( layoutRight.value.value !== right )
        {
            const {layoutRightProp} = componentDesc;

            const newValue = ANumber.fromNumber(right);
            const newFieldValue = new Units(newValue, layoutRight.unitsType);

            layoutRightProp.setPropValue(PropPath.ROOT, newFieldValue);
        }
    }

    public static recalcLeft(props:LayoutRecalcDimensionsProps)
    {
        const {componentDesc, boxClientRect, hostClientRect} = props;
        const {layoutLeft} = componentDesc;

        const hostWidth = hostClientRect.width;

        let left;
        let width;
        let right;

        if( boxClientRect )
        {
            left = boxClientRect.left - hostClientRect.left;
        }
        else
        {
            const {layoutRight, layoutWidth} = componentDesc;

            if( UnitsType.percentages().isEqualTo(layoutRight.unitsType) )
                right = hostWidth * layoutRight.value.value / 100;
            else
                right = layoutRight.value.value;

            if( UnitsType.percentages().isEqualTo(layoutWidth.unitsType) )
                width = hostWidth * layoutWidth.value.value / 100;
            else
                width = layoutWidth.value.value;

            left = Math.round(hostWidth - right - width);
        }

        if( UnitsType.percentages().isEqualTo(layoutLeft.unitsType) )
        {
            left = (left / hostWidth) * 100;
        }

        if( layoutLeft.value.value !== left )
        {
            const {layoutLeftProp} = componentDesc;

            const newValue = ANumber.fromNumber(left);
            const newFieldValue = new Units(newValue, layoutLeft.unitsType);

            layoutLeftProp.setPropValue(PropPath.ROOT, newFieldValue);
        }
    }

    public static recalcRight(props:LayoutRecalcDimensionsProps)
    {
        const {componentDesc, boxClientRect, hostClientRect} = props;
        const {layoutRight} = componentDesc;

        const hostWidth = hostClientRect.width;

        let left;
        let width;
        let right;

        if( boxClientRect )
        {
            left = boxClientRect.left - hostClientRect.left;
            width = boxClientRect.width;
        }
        else
        {
            const {layoutLeft, layoutWidth} = componentDesc;

            if( UnitsType.percentages().isEqualTo(layoutLeft.unitsType) )
                left = hostWidth * layoutLeft.value.value / 100;
            else
                left = layoutLeft.value.value;

            if( UnitsType.percentages().isEqualTo(layoutWidth.unitsType) )
                width = hostWidth * layoutWidth.value.value / 100;
            else
                width = layoutWidth.value.value;
        }

        right = Math.round(hostWidth - left - width);

        if( UnitsType.percentages().isEqualTo(layoutRight.unitsType) )
        {
            right = (right / hostWidth) * 100;
        }

        if( layoutRight.value.value !== right )
        {
            const {layoutRightProp} = componentDesc;

            const newValue = ANumber.fromNumber(right);
            const newFieldValue = new Units(newValue, layoutRight.unitsType);

            layoutRightProp.setPropValue(PropPath.ROOT, newFieldValue);
        }
    }

    public static recalcTop(props:LayoutRecalcDimensionsProps)
    {
        const {componentDesc, boxClientRect, hostClientRect} = props;
        const {layoutTop} = componentDesc;

        const hostHeight = hostClientRect.height;

        let top;
        let height;
        let bottom;

        if( boxClientRect )
        {
            top = boxClientRect.top - hostClientRect.top;
        }
        else
        {
            const {layoutBottom, layoutHeight} = componentDesc;

            if( UnitsType.percentages().isEqualTo(layoutBottom.unitsType) )
                bottom = hostHeight * layoutBottom.value.value / 100;
            else
                bottom = layoutBottom.value.value;

            if( UnitsType.percentages().isEqualTo(layoutHeight.unitsType) )
                height = hostHeight * layoutHeight.value.value / 100;
            else
                height = layoutHeight.value.value;

            top = Math.round(hostHeight - bottom - height);
        }

        if( UnitsType.percentages().isEqualTo(layoutTop.unitsType) )
        {
            top = (top / hostHeight) * 100;
        }

        if( layoutTop.value.value !== top )
        {
            const {layoutTopProp} = componentDesc;

            const newValue = ANumber.fromNumber(top);
            const newFieldValue = new Units(newValue, layoutTop.unitsType);

            layoutTopProp.setPropValue(PropPath.ROOT, newFieldValue);
        }
    }

    public static recalcBottom(props:LayoutRecalcDimensionsProps)
    {
        const {componentDesc, boxClientRect, hostClientRect} = props;
        const {layoutBottom} = componentDesc;

        const hostHeight = hostClientRect.height;

        let top;
        let height;
        let bottom;

        if( boxClientRect )
        {
            top = boxClientRect.top - hostClientRect.top;
            height = boxClientRect.height;
        }
        else
        {
            const {layoutTop, layoutHeight} = componentDesc;

            if( UnitsType.percentages().isEqualTo(layoutTop.unitsType) )
                top = hostHeight * layoutTop.value.value / 100;
            else
                top = layoutTop.value.value;

            if( UnitsType.percentages().isEqualTo(layoutHeight.unitsType) )
                height = hostHeight * layoutHeight.value.value / 100;
            else
                height = layoutHeight.value.value;
        }

        bottom = Math.round(hostHeight - top - height);

        // console.log('%c bottom 1 =', 'background:#ff0;color:#000;', bottom);

        if( UnitsType.percentages().isEqualTo(layoutBottom.unitsType) )
        {
            bottom = (bottom / hostHeight) * 100;
        }

        // console.log('%c bottom 2 =', 'background:#ff0;color:#000;', bottom, hostClientRect.height, top, height);

        // if( Math.abs(layoutBottom.value.value - bottom) > 10 )
        // {
        //     console.log('%c ??? =', 'background:#f00;color:#ff0;', props);
        //     console.log('%c ??? =', 'background:#f00;color:#ff0;', hostClientRect.height, top, height);
        // }

        if( layoutBottom.value.value !== bottom )
        {
            const {layoutBottomProp} = componentDesc;

            const newValue = ANumber.fromNumber(bottom);
            const newFieldValue = new Units(newValue, layoutBottom.unitsType);

            layoutBottomProp.setPropValue(PropPath.ROOT, newFieldValue);
        }
    }

    public static resize(direction:CompassDir, componentDesc:ComponentDesc, hostClientRect:ClientRect):void
    {
        const props:LayoutRecalcDimensionsProps = {
            componentDesc,
            hostClientRect,
            layoutRecalc: LayoutRecalc.RESIZE
        };

        switch( direction )
        {
            case CompassDir.North:     // TODO up -> top height
                break;

            case CompassDir.South:     // TODO down -> height
                LayoutUtils.recalcBottom(props);
                break;

            case CompassDir.West:      // TODO left -> left width
                break;

            case CompassDir.East:      // TODO right -> width
                LayoutUtils.recalcRight(props);
                break;

            case CompassDir.NorthWest: // TODO up left -> top left width height
                break;

            case CompassDir.NorthEast: // TODO up right -> top width height
                LayoutUtils.recalcRight(props);
                break;

            case CompassDir.SouthWest: // TODO down left -> left width height
                LayoutUtils.recalcBottom(props);
                break;

            case CompassDir.SouthEast: // TODO down right -> width height
                LayoutUtils.recalcRight(props);
                LayoutUtils.recalcBottom(props);
                break;
        }
    }

    public static getParentHostClientRect(box:Box):ClientRect | null
    {
        if( !box || box.isViewport ) return null;

        let searchStr;
        let searchInTabsStr = '';

        if( !box.parent ) // TODO: outside the Viewport -> take Viewport
        {
            // const {surfaceArea} = controllers.ui;
            // const activePage:Page = surfaceArea.getActivePage();
            // const viewportId = rootState.ui.visualDesigner.openDesign.designState.surface.activePage.artboard.viewport.id;
            const pageId = rootState.ui.visualDesigner.openDesign.designState.surface.activePage.id;

            searchStr = `[data-page-id="${pageId}"]`;
        }
        else
        {
            searchStr = `[data-host-box-id="${box.parent.id}"]`;

            if( box.hostId ) // box in TABS
                searchInTabsStr = `[data-host-id="${box.hostId}"]`;
        }

        let hostNodeRef;

        hostNodeRef = document.querySelector(searchStr + searchInTabsStr);

        if( !hostNodeRef ) return null;

        return LayoutUtils.getBoundingClientRect(hostNodeRef);
    }

    public static getBoxClientRect(box:Box):ClientRect | null
    {
        const boxNodeRef = document.querySelector(`[data-box-id="${box.componentDesc.box.id}"]`);

        if( !boxNodeRef ) return; // in NOT active TAB

        return LayoutUtils.getBoundingClientRect(boxNodeRef);
    }

    public static getBoundingClientRect(nodeRef:Element):ClientRect
    {
        const {area: {zoom}} = rootState.ui.visualDesigner.surfaceEditor;

        const clientRect = nodeRef.getBoundingClientRect();

        const top = Math.round(clientRect.top / zoom);
        const left = Math.round(clientRect.left / zoom);
        const right = Math.round(clientRect.right / zoom);
        const bottom = Math.round(clientRect.bottom / zoom);

        return Bounds.from(
            left,
            top,
            // Math.round(clientRect.right - clientRect.left),
            // Math.round(clientRect.bottom - clientRect.top)
            // Math.round(clientRect.width + (clientRect.left - Math.floor(clientRect.left))),
            // Math.round(clientRect.height + (clientRect.top - Math.floor(clientRect.top))),
            Math.round(right - left),
            Math.round(bottom - top)
        ).toClientRect();
    }

    public static areLayoutPropsHasPercentages(box:Box):boolean
    {
        const {layoutLeft, layoutTop, layoutRight, layoutBottom, layoutWidth, layoutHeight} = box.componentDesc;

        return UnitsType.percentages().isEqualTo(layoutTop.unitsType) ||
            UnitsType.percentages().isEqualTo(layoutLeft.unitsType) ||
            UnitsType.percentages().isEqualTo(layoutWidth.unitsType) ||
            UnitsType.percentages().isEqualTo(layoutHeight.unitsType) ||
            UnitsType.percentages().isEqualTo(layoutRight.unitsType) ||
            UnitsType.percentages().isEqualTo(layoutBottom.unitsType);

    }

    private static layoutChanged(oldLayout:LayoutModel, newLayout:LayoutModel, componentDesc:ComponentDesc)
    {
        const {positioningType: oldPositioningType} = oldLayout;
        const {positioningType} = newLayout;

        if( positioningType && !positioningType.isEqualTo(oldPositioningType) )
        {
            if( PositioningType.isParentDirected(positioningType) )
            {
                const {layoutWidthProp, layoutHeightProp, layoutProp} = componentDesc;
                const {childLayout} = componentDesc.box.parent.componentDesc;
                const isRowDirection:boolean = DirectionType.isRow(childLayout && childLayout.hvBox.direction)
                    || DirectionType.isRowReversed(childLayout && childLayout.hvBox.direction);
                const mainAxisSizeProp:Prop<PositiveUnits> = isRowDirection ? layoutWidthProp : layoutHeightProp;
                const crossAxisSizeProp:Prop<PositiveUnits> = isRowDirection ? layoutHeightProp : layoutWidthProp;

                layoutProp.setPropValue(
                    LayoutUtils.HV_BOX_MAIN_AXIS_SIZE_PATH,
                    mainAxisSizeProp.value.unitsString
                );
                layoutProp.setPropValue(
                    LayoutUtils.HV_BOX_CROSS_AXIS_SIZE_PATH,
                    crossAxisSizeProp.value.unitsString
                );
            }
            else
            {
                const {repeaterProp, layoutHorizontal, layoutVertical} = componentDesc;
                const repeaterModel:RepeaterModel = repeaterProp.value;

                if( RepeaterDisplayType.isTiles(repeaterModel.displayType)
                    && (LayoutHorizontal.isLeftRight(layoutHorizontal)
                        || LayoutHorizontal.isRight(layoutHorizontal)
                        || LayoutVertical.isTopBottom(layoutVertical)
                        || LayoutVertical.isBottom(layoutVertical)) )
                {
                    repeaterProp.setPropValue(
                        PropPath.fromFieldPath(RepeaterModelFieldPaths.DISPLAY_TYPE_FIELD),
                        RepeaterDisplayType.vertical()
                    );
                }
            }
        }

    }

    private static childLayoutChanged(oldLayout:ChildLayoutModel, newLayout:ChildLayoutModel, componentDesc:ComponentDesc)
    {
        const {hvBox: {direction: oldDirection}} = oldLayout;
        const {hvBox: {direction}} = newLayout;

        if( direction && !direction.isEqualTo(oldDirection) )
        {
            const {box: {children}} = componentDesc;

            children.forEach((childBox:Box) =>
            {
                const {componentDesc: {layoutProp}} = childBox;
                const mainAxisSize:AString = layoutProp.getPropValue(LayoutUtils.HV_BOX_MAIN_AXIS_SIZE_PATH);
                const crossAxisSize:AString = layoutProp.getPropValue(LayoutUtils.HV_BOX_CROSS_AXIS_SIZE_PATH);

                if( /\d+px$/i.test(mainAxisSize.value) && /\d+px$/i.test(crossAxisSize.value) )
                {
                    const mainAxisMinSize:AString = layoutProp.getPropValue(
                        LayoutUtils.HV_BOX_MAIN_AXIS_MIN_SIZE_PATH
                    );
                    const crossAxisMinSize:AString = layoutProp.getPropValue(
                        LayoutUtils.HV_BOX_CROSS_AXIS_MIN_SIZE_PATH
                    );
                    const mainAxisMaxSize:AString = layoutProp.getPropValue(
                        LayoutUtils.HV_BOX_MAIN_AXIS_MAX_SIZE_PATH
                    );
                    const crossAxisMaxSize:AString = layoutProp.getPropValue(
                        LayoutUtils.HV_BOX_CROSS_AXIS_MAX_SIZE_PATH
                    );

                    layoutProp.setPropValue(
                        LayoutUtils.HV_BOX_MAIN_AXIS_SIZE_PATH,
                        crossAxisSize
                    );
                    layoutProp.setPropValue(
                        LayoutUtils.HV_BOX_CROSS_AXIS_SIZE_PATH,
                        mainAxisSize
                    );
                    layoutProp.setPropValue(
                        LayoutUtils.HV_BOX_MAIN_AXIS_MIN_SIZE_PATH,
                        crossAxisMinSize
                    );
                    layoutProp.setPropValue(
                        LayoutUtils.HV_BOX_CROSS_AXIS_MIN_SIZE_PATH,
                        mainAxisMinSize
                    );
                    layoutProp.setPropValue(
                        LayoutUtils.HV_BOX_MAIN_AXIS_MAX_SIZE_PATH,
                        crossAxisMaxSize
                    );
                    layoutProp.setPropValue(
                        LayoutUtils.HV_BOX_CROSS_AXIS_MAX_SIZE_PATH,
                        mainAxisMaxSize
                    );
                }
            });
        }

    }

    private static readonly HV_BOX_MAIN_AXIS_SIZE_PATH:PropPath = PropPath.fromFieldPath(LayoutModelFieldPaths.HV_BOX_MAIN_AXIS_SIZE_FIELD);
    private static readonly HV_BOX_CROSS_AXIS_SIZE_PATH:PropPath = PropPath.fromFieldPath(LayoutModelFieldPaths.HV_BOX_CROSS_AXIS_SIZE_FIELD);
    private static readonly HV_BOX_MAIN_AXIS_MIN_SIZE_PATH:PropPath = PropPath.fromFieldPath(LayoutModelFieldPaths.HV_BOX_MAIN_AXIS_MIN_SIZE_FIELD);
    private static readonly HV_BOX_CROSS_AXIS_MIN_SIZE_PATH:PropPath = PropPath.fromFieldPath(LayoutModelFieldPaths.HV_BOX_CROSS_AXIS_MIN_SIZE_FIELD);
    private static readonly HV_BOX_MAIN_AXIS_MAX_SIZE_PATH:PropPath = PropPath.fromFieldPath(LayoutModelFieldPaths.HV_BOX_MAIN_AXIS_MAX_SIZE_FIELD);
    private static readonly HV_BOX_CROSS_AXIS_MAX_SIZE_PATH:PropPath = PropPath.fromFieldPath(LayoutModelFieldPaths.HV_BOX_CROSS_AXIS_MAX_SIZE_FIELD);
}
